import { RiCalendarCheckLine, RiFileListLine, RiLineChartLine, RiSparklingLine } from "@remixicon/react"
import { useState, useEffect } from "react"

export default function CharlestonProcess() {
  const [activeStep, setActiveStep] = useState(0)

  const steps = [
    {
      icon: RiCalendarCheckLine,
      number: "01",
      title: "Book a Consultation",
      description: "Schedule your free consultation to discuss your business goals and challenges.",
      color: "from-[#003566] to-[#001d3d]",
      gradient: "bg-gradient-to-br from-[#003566]/10 to-[#001d3d]/5",
      bgColor: "bg-[#003566]/5",
    },
    {
      icon: RiFileListLine,
      number: "02",
      title: "Review Analysis and Proposal",
      description: "We analyze your current presence and create a custom strategy for growth.",
      color: "from-[#001d3d] to-[#000814]",
      gradient: "bg-gradient-to-br from-[#001d3d]/10 to-[#000814]/5",
      bgColor: "bg-[#001d3d]/5",
    },
    {
      icon: RiLineChartLine,
      number: "03",
      title: "Track Results",
      description: "Monitor your progress with detailed analytics and regular reporting.",
      color: "from-[#000814] to-[#003566]",
      gradient: "bg-gradient-to-br from-[#000814]/10 to-[#003566]/5",
      bgColor: "bg-[#000814]/5",
    },
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [steps.length])

  return (
    <section
      aria-label="How It Works"
      id="process"
      className="relative mx-auto max-w-7xl px-4 scroll-my-24"
    >
      {/* Enhanced animated background elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#06b6d4]/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 right-0 w-[600px] h-[600px] bg-[#003566]/15 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-[#0ea5e9]/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
      </div>

      <div className="text-center mb-20">
        <div className="inline-flex items-center justify-center gap-2 mb-4">
          <RiSparklingLine className="size-6 text-[#06b6d4] animate-pulse" />
          <h2 className="relative inline-block text-lg font-semibold tracking-tight text-[#003566]">
            How It Works
            <div className="absolute top-1 -left-[8px] h-5 w-[3px] rounded-r-sm bg-[#06b6d4]" />
          </h2>
          <RiSparklingLine className="size-6 text-[#06b6d4] animate-pulse" />
        </div>
        <p className="mt-4 text-4xl font-semibold tracking-tighter text-balance text-gray-900 md:text-5xl">
          Simple Process, Powerful Results
        </p>
        <p className="mt-4 text-lg text-balance text-gray-700 max-w-3xl mx-auto">
          Getting started is easy, just three simple steps to transform your business.
        </p>
      </div>

      {/* Desktop view with enhanced animations */}
      <div className="hidden lg:block relative">
        {/* Animated connecting path with particles */}
        <svg className="absolute top-32 left-0 w-full h-1 -z-10" style={{ transform: 'translateY(-50%)' }}>
          <defs>
            <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#003566" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#003566" stopOpacity="0.3" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <line
            x1="20%"
            y1="0"
            x2="80%"
            y2="0"
            stroke="url(#pathGradient)"
            strokeWidth="3"
            strokeDasharray="8 4"
            filter="url(#glow)"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="24"
              dur="2s"
              repeatCount="indefinite"
            />
          </line>
        </svg>

        <div className="grid grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon
            const isActive = activeStep === index

            return (
              <div
                key={index}
                className="relative group cursor-pointer"
                onMouseEnter={() => setActiveStep(index)}
              >
                <div
                  className={`relative bg-white rounded-3xl border-2 p-8 shadow-lg transition-all duration-500 transform overflow-hidden ${
                    isActive
                      ? 'border-[#06b6d4] shadow-2xl shadow-[#06b6d4]/30 -translate-y-6 scale-105'
                      : 'border-gray-200 hover:border-[#003566] hover:-translate-y-2'
                  }`}
                >
                  {/* Animated background gradient */}
                  <div className={`absolute inset-0 rounded-3xl ${step.gradient} opacity-0 transition-opacity duration-500 ${isActive ? 'opacity-100' : ''}`} />

                  {/* Animated particles */}
                  {isActive && (
                    <>
                      <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-[#06b6d4] animate-ping" style={{ animationDelay: '0s' }} />
                      <div className="absolute top-8 right-8 w-1.5 h-1.5 rounded-full bg-[#0ea5e9] animate-ping" style={{ animationDelay: '0.5s' }} />
                      <div className="absolute bottom-8 left-8 w-2 h-2 rounded-full bg-[#06b6d4] animate-ping" style={{ animationDelay: '1s' }} />
                    </>
                  )}

                  {/* Content */}
                  <div className="relative z-10">
                    <div className={`inline-flex items-center justify-center rounded-2xl bg-gradient-to-br ${step.color} p-4 shadow-lg mb-6 transition-all duration-500 ${
                      isActive ? 'scale-110 rotate-3 shadow-xl shadow-[#06b6d4]/30' : 'group-hover:scale-105'
                    }`}>
                      <Icon className={`size-8 text-white transition-all duration-500 ${isActive ? 'animate-bounce' : ''}`} />
                    </div>

                    <div className={`text-6xl font-bold mb-2 transition-all duration-500 ${
                      isActive ? 'text-[#06b6d4] drop-shadow-lg' : 'text-gray-200'
                    }`}>
                      {step.number}
                    </div>

                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {step.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Pulse indicator for active step */}
                  {isActive && (
                    <>
                      <div className="absolute -top-3 -right-3">
                        <div className="relative">
                          <div className="w-6 h-6 bg-[#06b6d4] rounded-full animate-ping" />
                          <div className="absolute top-0 left-0 w-6 h-6 bg-[#06b6d4] rounded-full flex items-center justify-center">
                            <div className="w-3 h-3 bg-white rounded-full" />
                          </div>
                        </div>
                      </div>
                      {/* Glowing border effect */}
                      <div className="absolute inset-0 rounded-3xl border-2 border-[#06b6d4] opacity-50 animate-pulse" />
                    </>
                  )}
                </div>

                {/* Enhanced arrow connector */}
                {index < steps.length - 1 && (
                  <div className="absolute top-20 -right-4 z-20 transform translate-x-1/2">
                    <div className={`transition-all duration-500 ${isActive ? 'scale-150' : 'scale-100'}`}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className={`transition-all duration-500 ${isActive ? 'text-[#06b6d4] drop-shadow-lg animate-pulse' : 'text-[#06b6d4]/60'}`}>
                        <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Mobile view with enhanced animations */}
      <div className="lg:hidden space-y-8">
        {steps.map((step, index) => {
          const Icon = step.icon
          const isActive = activeStep === index

          return (
            <div key={index} className="relative">
              {/* Connecting line */}
              {index < steps.length - 1 && (
                <div className="absolute top-full left-14 w-1 h-8 bg-gradient-to-b from-[#06b6d4] to-[#003566] z-0">
                  <div className="absolute inset-0 bg-gradient-to-b from-[#06b6d4] to-[#003566] animate-pulse" />
                </div>
              )}

              <div
                className={`relative bg-white rounded-2xl border-2 p-6 shadow-lg transition-all duration-500 overflow-hidden ${
                  isActive
                    ? 'border-[#06b6d4] shadow-xl shadow-[#06b6d4]/30 scale-105'
                    : 'border-gray-200'
                }`}
              >
                <div className={`absolute inset-0 rounded-2xl ${step.gradient} opacity-0 transition-opacity duration-500 ${isActive ? 'opacity-100' : ''}`} />

                {/* Animated particles for mobile */}
                {isActive && (
                  <>
                    <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-[#06b6d4] animate-ping" />
                    <div className="absolute bottom-4 right-8 w-1.5 h-1.5 rounded-full bg-[#0ea5e9] animate-ping" style={{ animationDelay: '0.5s' }} />
                  </>
                )}

                <div className="relative z-10 flex items-start gap-6">
                  <div className={`flex-shrink-0 inline-flex items-center justify-center rounded-xl bg-gradient-to-br ${step.color} p-4 shadow-lg transition-all duration-500 ${
                    isActive ? 'scale-110 shadow-[#06b6d4]/30' : ''
                  }`}>
                    <Icon className={`size-6 text-white ${isActive ? 'animate-pulse' : ''}`} />
                  </div>

                  <div className="flex-1">
                    <div className={`text-4xl font-bold mb-1 transition-colors duration-500 ${
                      isActive ? 'text-[#06b6d4]' : 'text-gray-200'
                    }`}>
                      {step.number}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Enhanced progress indicators */}
      <div className="mt-12 flex justify-center gap-3">
        {steps.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveStep(index)}
            className={`h-2 rounded-full transition-all duration-500 ${
              activeStep === index
                ? 'w-12 bg-[#06b6d4] shadow-lg shadow-[#06b6d4]/50'
                : 'w-2 bg-gray-300 hover:bg-gray-400 hover:w-4'
            }`}
            aria-label={`Go to step ${index + 1}`}
          />
        ))}
      </div>

      {/* Call to action */}
      <div className="mt-16 text-center">
        <p className="text-lg text-gray-700 mb-6">
          Ready to get started?
        </p>
        <a
          href="https://airtable.com/appR9wgPnwfYKJA5f/pagEhOnZkUUuOZskP/form"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex cursor-pointer items-center justify-center rounded-md border-b-[1.5px] border-[#0ea5e9] bg-gradient-to-b from-[#06b6d4] to-[#0ea5e9] px-8 py-4 font-medium text-white shadow-[0_0_0_2px_rgba(14,165,233,0.1),0_0_20px_0_rgba(14,165,233,0.4)] transition-all duration-200 ease-in-out hover:shadow-[0_0_0_2px_rgba(14,165,233,0.15),0_0_28px_0_rgba(14,165,233,0.6)] hover:scale-105"
        >
          Schedule Your Free Consultation
        </a>
      </div>
    </section>
  )
}
